import gql from 'graphql-tag';
import { Fragment, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import className from 'classnames/bind';
import { DonateFormFragment } from '@graphqlTypes';
import { Button, Container, HeadlineAnim, Image, MaterialLoader, RevealWrapper } from '@components';
import { getHighlightMarkup } from '@/core/utils';
import { RadioButton, FormInput } from '../FormComponents';
import ButtonIcon from '@/assets/svg/external-arrow.svg';

import { useViewSize } from '@/providers/ViewSizeContext';
import { useInView } from 'motion/react';
import { AnimVals } from '@/utils/animUtils';

import styles from './DonateForm.module.scss';
import { easeOutQuad } from '@/core/easing';
import { usePageWrapperData } from '@/providers/PageWrapperContext';
const cx = className.bind(styles);

type AmountButtonProps = {
  formAmount: DonateFormFragment['formAmounts'][number];
  onClick: (value: string) => void;
  active: boolean;
}

const AmountButton = ({ formAmount, onClick, active }: AmountButtonProps) => {
  const { amount, otherLabel } = formAmount;
  const value = otherLabel ? 'other' : amount ? amount.toString() : '';
  const label = otherLabel ? otherLabel : amount ? `$${amount}` : '';
  return (
    <RadioButton
      className={cx("amount-button")}
      onClick={onClick}
      active={active}
      value={value}
      label={label}
    />
  )
}

export default function DonateForm(props: DonateFormFragment) {
  const { eyebrow, headline, subhead, formHeadline, ctaLabel, otherInputLabel, formAmounts, image, minDonationError } = props;
  const headlineMarkup = getHighlightMarkup(headline);
  const [otherValue, setOtherValue] = useState<string>('');
  const [selectedAmount, setSelectedAmount] = useState<string>('');
  const [formLoading, setFormLoading] = useState<boolean>(false);
  const [ticketureId, setTicketureId] = useState<string>('');
  const [error, setError] = useState<string>('');
  const { labels } = usePageWrapperData();

  const otherSelected = selectedAmount === 'other';

  const { isDesktop } = useViewSize();
  const rootEl = useRef<HTMLDivElement>(null);
  const REVEAL = AnimVals;
  const triggerPercent = isDesktop ? REVEAL.lg.triggerPercent : REVEAL.sm.triggerPercent;
  const isInView = useInView(rootEl, { amount: triggerPercent, once: REVEAL.once });

  const handleAmountSelect = (value: string, ticketureId: string) => {
    setError('');
    setSelectedAmount(value);
    setTicketureId(ticketureId);
  };

  const onOtherInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setError('');
    const sanitizedValue = value?.replace(/[^0-9.]/g, '').replace(/(\..*?)\..*/g, '$1').replace(/^0[^.]/, '0');
    setOtherValue(sanitizedValue);
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setFormLoading(true);
    const res = await fetch('/api/tickets/donate/', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        amount: otherSelected ? otherValue : selectedAmount,
        ticketureId
      }),
    }).then(data => data.json());
    if (res?.success && res?.data?.checkout_url) {
      window.location.href = res.data.checkout_url;
    } else {
      if (res.message === "currency_amount_min_not_met") {
        setError(minDonationError);
      } else {
        setError(labels?.genericFormError);
      }
    }
    setFormLoading(false);
  }

  return (
    <section className={cx(["donate-form"])} ref={rootEl}>
      <Container>
        <RevealWrapper isInView={isInView}>
          <div className={cx("row")}>
            <div className={cx(["content-wrapper", "col-12", "col-lg-5"])}>
              <div className={cx("copy")}>
                {eyebrow && <p className={cx("eyebrow-l", "highlighted-text")}>{eyebrow}</p>}
                {headlineMarkup && <HeadlineAnim
                  tag="h2"
                  text={headlineMarkup}
                  className={cx('title-uc-l')}
                  isRevealEnabled={true}
                  isInView={isInView}
                />}
                {subhead && <p className={cx("subhead", "text-l")}>{subhead}</p>}
              </div>
              <div className={cx("form-wrapper")}>
                {formHeadline && <h3 className={cx("form-headline", "text-l")}>{formHeadline}</h3>}
                <form className={cx("form")} onSubmit={handleFormSubmit}>
                  {
                    <AnimatePresence>
                      {formLoading &&
                        <motion.div
                          className={cx("form__loader")}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          transition={{ duration: 0.3, ease: easeOutQuad }}
                        >
                          <MaterialLoader className={cx("form__loader__loader")} />
                        </motion.div>
                      }
                    </AnimatePresence>
                  }
                  <div className={cx("amounts")}>
                    {formAmounts?.map((formAmount, index) => (
                      <Fragment key={index}>
                        <AmountButton
                          formAmount={formAmount}
                          onClick={(value: string) => handleAmountSelect(value, formAmount.ticketureId)}
                          active={selectedAmount === formAmount.amount?.toString() || (formAmount.type?.[0] === 'other' && selectedAmount === 'other')}
                        />
                        { formAmount.type?.[0] === 'other' && (
                          <motion.div
                            className={cx("other-input-wrapper")}
                            initial={{ height: 0 }}
                            animate={{ height: otherSelected ? 'auto' : 0 }}
                            transition={{ duration: 0.3, ease: easeOutQuad }}
                          >
                            <FormInput
                              className={cx("other-input")}
                              name="Other Amount"
                              placeholder={otherInputLabel}
                              value={otherValue}
                              min={1}
                              step={0.01}
                              required={otherSelected}
                              tabIndex={otherSelected ? 0 : -1}
                              onChange={onOtherInputChange}
                              width="full"
                            />
                          </motion.div>
                        )}
                      </Fragment>
                    ))}
                  </div>
                  <div className={cx("form-bottom")}>
                    <AnimatePresence>
                      { error && (
                        <motion.p
                          className={cx("form-error", "text-s")}
                          initial={{ opacity: 0, y: '0%' }}
                          animate={{ opacity: 1, y: '-50%' }}
                          exit={{ opacity: 0, y: '0%' }}
                          transition={{ duration: 0.3, ease: easeOutQuad }}
                        >
                          {error}
                        </motion.p>
                      )}
                    </AnimatePresence>
                    <Button
                      className={cx("submit-button")}
                      type="submit"
                      label={ctaLabel}
                      disabled={!selectedAmount || (otherSelected && !otherValue)}
                      icon={ButtonIcon}
                    />
                  </div>
                </form>
              </div>
            </div>
            <Image
              className={cx(["image", "col-12", "col-lg-6"])}
              image={image}
            />
          </div>
        </RevealWrapper>
      </Container>
    </section>
  )
}

DonateForm.fragments = {
  entry: gql`
    fragment DonateForm on FlexiblePageContentContentDonateFormLayout {
      eyebrow
      headline
      subhead
      formHeadline
      otherInputLabel
      minDonationError
      ctaLabel
      formAmounts {
        type
        ticketureId
        amount
        otherLabel
      }
      image {
        ...ACFMedia
      }
    }
  `,
};